import type { Escola, MatriculaEscola, MatriculaTurnos, NivelEnsinoR065 } from '../types/database'
import { NIVEIS_R065 } from '../types/database'
import { turnosFromMatricula } from './matriculasEscola'

export const TURNOS_ESCOLA_UI = [
  { key: 'm', label: 'Manhã' },
  { key: 't', label: 'Tarde' },
  { key: 'n', label: 'Noite' },
  { key: 'i', label: 'Integral' },
] as const

export type TurnoEscolaKey = (typeof TURNOS_ESCOLA_UI)[number]['key']

const TURNOS_KEYS = {
  infantil: { m: 'infantil_m', t: 'infantil_t', n: 'infantil_n', i: 'infantil_i' },
  fund_menor: { m: 'fund_menor_m', t: 'fund_menor_t', n: 'fund_menor_n', i: 'fund_menor_i' },
  fund_maior: { m: 'fund_maior_m', t: 'fund_maior_t', n: 'fund_maior_n', i: 'fund_maior_i' },
  eja: { m: 'eja_m', t: 'eja_t', n: 'eja_n', i: 'eja_i' },
} as const satisfies Record<NivelEnsinoR065, Record<TurnoEscolaKey, keyof Escola>>

export type TurnoEscolaFieldKey =
  (typeof TURNOS_KEYS)[NivelEnsinoR065][TurnoEscolaKey]

export type TurnosEscolaForm = Record<TurnoEscolaFieldKey, number>

export type EscolaTurnosRecord = Pick<Escola, TurnoEscolaFieldKey>

/** Turnos do cadastro da escola (fonte após importação R065). */
export function turnosFromEscola(e: Escola, nivel: NivelEnsinoR065): MatriculaTurnos {
  const keys = TURNOS_KEYS[nivel]
  const m = Number(e[keys.m]) || 0
  const t = Number(e[keys.t]) || 0
  const n = Number(e[keys.n]) || 0
  const i = Number(e[keys.i]) || 0
  return { m, t, n, i, total: m + t + n + i }
}

export function escolaTemTurnosCadastrados(e: Escola): boolean {
  const niveis: NivelEnsinoR065[] = ['infantil', 'fund_menor', 'fund_maior', 'eja']
  return niveis.some((nivel) => {
    const t = turnosFromEscola(e, nivel)
    return t.m > 0 || t.t > 0 || t.n > 0 || t.i > 0
  })
}

/** Flag explícita de preenchimento/ajuste manual (US-R05). */
export function escolaTemTurnosManuais(e: Pick<Escola, 'turnos_manuais'>): boolean {
  return !!e.turnos_manuais
}

/** Shape mínimo do R065 para comparar com o cadastro da escola. */
export type TurnosR065Comparavel = {
  infantil: MatriculaTurnos
  fundMenor: MatriculaTurnos
  fundMaior: MatriculaTurnos
  eja: MatriculaTurnos
}

function turnosNivelIgual(a: MatriculaTurnos, b: MatriculaTurnos): boolean {
  return a.m === b.m && a.t === b.t && a.n === b.n && a.i === b.i
}

/**
 * True quando a escola tem turnos manuais ativos e algum turno diverge do PDF R065.
 * Sem a flag, não há “divergência de cadastro manual” a alertar.
 */
export function turnosEscolaDivergeDeR065(
  escola: Escola,
  r065: TurnosR065Comparavel,
): boolean {
  if (!escolaTemTurnosManuais(escola)) return false
  return (
    !turnosNivelIgual(turnosFromEscola(escola, 'infantil'), r065.infantil) ||
    !turnosNivelIgual(turnosFromEscola(escola, 'fund_menor'), r065.fundMenor) ||
    !turnosNivelIgual(turnosFromEscola(escola, 'fund_maior'), r065.fundMaior) ||
    !turnosNivelIgual(turnosFromEscola(escola, 'eja'), r065.eja)
  )
}

export type DivergenciaTurnoManual = {
  codigo_semed: string
  nome: string
}

/** Escolas com flag manual cujo cadastro diverge do PDF (para alerta na importação). */
export function listarDivergenciasTurnosManuais(
  escolas: Escola[],
  parsed: Array<TurnosR065Comparavel & { codigo: string; nome: string }>,
): DivergenciaTurnoManual[] {
  const byCodigo = new Map(
    escolas
      .filter((e) => escolaTemTurnosManuais(e) && e.codigo_semed != null)
      .map((e) => [String(e.codigo_semed), e]),
  )
  const out: DivergenciaTurnoManual[] = []
  for (const row of parsed) {
    const escola = byCodigo.get(row.codigo)
    if (!escola) continue
    if (turnosEscolaDivergeDeR065(escola, row)) {
      out.push({ codigo_semed: row.codigo, nome: escola.nome || row.nome })
    }
  }
  return out
}

export function mensagemAlertaDivergenciasTurnosManuais(
  divergencias: DivergenciaTurnoManual[],
): string {
  if (!divergencias.length) return ''
  const amostra = divergencias
    .slice(0, 5)
    .map((d) => `${d.codigo_semed} (${d.nome})`)
    .join(', ')
  const mais =
    divergencias.length > 5 ? ` e mais ${divergencias.length - 5}` : ''
  return (
    `A importação diverge do cadastro manual em ${divergencias.length} escola(s): ` +
    `${amostra}${mais}. Os turnos manuais dessas escolas serão preservados.`
  )
}

/** Preferência: cadastro escolas → matrícula importada (preview). */
export function turnosEscolaOuMatricula(
  escola: Escola,
  matricula: MatriculaEscola | null | undefined,
  nivel: NivelEnsinoR065,
): MatriculaTurnos | null {
  if (escolaTemTurnosCadastrados(escola)) return turnosFromEscola(escola, nivel)
  if (matricula) return turnosFromMatricula(matricula, nivel)
  return null
}

/** Soma por turno em todos os níveis (visão escola). */
export function somaTurnosEscola(e: Escola): MatriculaTurnos {
  const niveis: NivelEnsinoR065[] = ['infantil', 'fund_menor', 'fund_maior', 'eja']
  const acc = { m: 0, t: 0, n: 0, i: 0, total: 0 }
  for (const nivel of niveis) {
    const t = turnosFromEscola(e, nivel)
    acc.m += t.m
    acc.t += t.t
    acc.n += t.n
    acc.i += t.i
  }
  acc.total = acc.m + acc.t + acc.n + acc.i
  return acc
}

/** Infantil não possui turno noturno. */
export function turnoEscolaHabilitado(nivel: NivelEnsinoR065, turno: TurnoEscolaKey): boolean {
  return !(nivel === 'infantil' && turno === 'n')
}

export function emptyTurnosEscolaForm(): TurnosEscolaForm {
  const form = {} as TurnosEscolaForm
  for (const { key: nivel } of NIVEIS_R065) {
    for (const { key: turno } of TURNOS_ESCOLA_UI) {
      form[TURNOS_KEYS[nivel][turno]] = 0
    }
  }
  return form
}

export function turnosFormFromRecord(e: Partial<EscolaTurnosRecord>): TurnosEscolaForm {
  const form = emptyTurnosEscolaForm()
  for (const field of Object.keys(form) as TurnoEscolaFieldKey[]) {
    form[field] = Math.max(0, Number(e[field]) || 0)
  }
  return form
}

export function turnosFormToRecordPayload(form: TurnosEscolaForm): Partial<EscolaTurnosRecord> {
  const payload: Partial<EscolaTurnosRecord> = {}
  for (const { key: nivel } of NIVEIS_R065) {
    for (const { key: turno } of TURNOS_ESCOLA_UI) {
      const field = TURNOS_KEYS[nivel][turno]
      payload[field] = turnoEscolaHabilitado(nivel, turno)
        ? Math.max(0, Math.floor(Number(form[field]) || 0))
        : 0
    }
  }
  return payload
}

export function totalNivelRecord(e: Partial<EscolaTurnosRecord>, nivel: NivelEnsinoR065): number {
  return turnosFromEscola(e as Escola, nivel).total
}

export function turnosEscolaFormFromEscola(e: Escola): TurnosEscolaForm {
  return turnosFormFromRecord(e)
}

/** Preferência: turnos no cadastro da escola; senão, matrícula R065 importada. */
export function turnosEscolaFormFromEscolaOuMatricula(
  escola: Escola,
  matricula?: MatriculaEscola | null,
): TurnosEscolaForm {
  if (escolaTemTurnosCadastrados(escola)) return turnosEscolaFormFromEscola(escola)
  if (matricula) {
    const form = emptyTurnosEscolaForm()
    for (const { key: nivel } of NIVEIS_R065) {
      const t = turnosFromMatricula(matricula, nivel)
      form[TURNOS_KEYS[nivel].m] = t.m
      form[TURNOS_KEYS[nivel].t] = t.t
      form[TURNOS_KEYS[nivel].n] = t.n
      form[TURNOS_KEYS[nivel].i] = t.i
    }
    return form
  }
  return turnosEscolaFormFromEscola(escola)
}

export function turnosEscolaFormToPayload(form: TurnosEscolaForm): Partial<Escola> {
  const payload: Partial<Escola> = {}
  let integral = 0
  for (const { key: nivel } of NIVEIS_R065) {
    let nivelTotal = 0
    for (const { key: turno } of TURNOS_ESCOLA_UI) {
      const field = TURNOS_KEYS[nivel][turno]
      const val = turnoEscolaHabilitado(nivel, turno)
        ? Math.max(0, Math.floor(Number(form[field]) || 0))
        : 0
      payload[field] = val
      nivelTotal += val
      if (turno === 'i') integral += val
    }
    if (nivel === 'infantil') payload.alunos_pre = nivelTotal
    if (nivel === 'fund_menor') payload.alunos_fund1 = nivelTotal
    if (nivel === 'fund_maior') payload.alunos_fund2 = nivelTotal
    if (nivel === 'eja') payload.alunos_eja = nivelTotal
  }
  payload.alunos_integral = integral
  return payload
}

/** Alunos elegíveis para uma refeição (regra global por turno) — flag Integral off. */
export function elegiveisRefeicaoPorTurno(turnos: MatriculaTurnos, refeicao: string): number {
  switch (refeicao) {
    case 'lanche_manha':
    case 'almoco':
      return turnos.m + turnos.i
    case 'lanche_tarde':
      return turnos.t + turnos.i
    case 'jantar':
      return turnos.n + turnos.i
    default:
      return 0
  }
}

/** Headcount parcial (sem I) quando cardápio integral é independente. */
export function elegiveisRefeicaoParcial(turnos: MatriculaTurnos, refeicao: string): number {
  switch (refeicao) {
    case 'lanche_manha':
    case 'almoco':
      return turnos.m
    case 'lanche_tarde':
      return turnos.t
    case 'jantar':
      return turnos.n
    default:
      return 0
  }
}

/** Headcount só Integral quando cardápio integral é independente. */
export function elegiveisRefeicaoIntegralOnly(turnos: MatriculaTurnos, refeicao: string): number {
  switch (refeicao) {
    case 'lanche_manha':
    case 'almoco':
    case 'lanche_tarde':
    case 'jantar':
      return turnos.i
    default:
      return 0
  }
}
